import { type DisplayLanguage, LANGUAGE_TO_LANGUAGE_DISPLAY, type Language, type Marketplace, PLATFORMS } from '@amzn/journeys-common';
import { sortBy } from 'es-toolkit/compat';
import { getLanguageOptionFromLanguageCodeAndMarketPlace } from 'features/campaign/campaign.util';
import { useMemoCompare } from 'hooks/useMemoCompare';
import pluralize from 'pluralize';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import type { FlavorPlatformAndVariantOption } from '../../campaign-configs/types';
import type { RegisteredMCCampaignConfig } from '../../campaign-types';
import { useValidateAllFlavorContent } from '../../content/validation/useValidateAllFlavorContent';
import { useCampaignConfig } from '../../hooks/useCampaignConfig';
import { useTPCampaignContext } from '../../TPCampaign/TPCampaignContext';
import { useFlavors } from '../../useFlavor';

type LanguageOptionValue = `${Marketplace}-${Language}`;

const getLangAndMpKey = (marketplace: Marketplace, language: Language): LanguageOptionValue => `${marketplace}-${language}`;

export type LanguageSelectOption = {
  label: DisplayLanguage;
  errorCount: number;
  language: Language;
  marketplace: Marketplace;
  value: LanguageOptionValue;
};

type FlavorInfo = {
  active: boolean;
  contentErrorsCount: number;
  language: Language;
  marketplace: Marketplace;
  optionKey: LanguageOptionValue;
  originalIndex: number;
  platform: PLATFORMS;
  variant?: string | null;
};

/**
 * What we need to render the correct content editor, name editor, and header
 */
type SelectedFlavorInfo = {
  headerText: string;
  key: string;
  language: Language;
  marketplace: Marketplace;
  originalIndex: number;
  platform: PLATFORMS;
  variant?: string | null;
};

type LanguageSelectOptionsMap = Partial<Record<LanguageOptionValue, LanguageSelectOption>>;

const getFlavorHeaderText = ({
  campaignConfig,
  flavorInfo,
  selectedLanguageOption,
}: {
  campaignConfig: RegisteredMCCampaignConfig;
  flavorInfo: FlavorInfo;
  selectedLanguageOption: LanguageSelectOption;
}): string => {
  if (campaignConfig.variants?.getVariantHeaderText) {
    return campaignConfig.variants.getVariantHeaderText(
      flavorInfo.variant as (typeof campaignConfig.variants.flavorPlatformAndVariantOptions)[number]['variant'],
    );
  }
  const languageLabel = selectedLanguageOption.label;
  const shouldUseLanguageLabelOnly = campaignConfig.hasMultiPlatformFlavors || campaignConfig.isPlatformAgnostic;
  if (shouldUseLanguageLabelOnly) return languageLabel;
  const platformPart =
    campaignConfig.platformNameOverrides?.[flavorInfo.platform] ?? (flavorInfo.platform === PLATFORMS.DESKTOP ? 'Desktop' : 'Mobile');
  return `${platformPart}, ${languageLabel}`;
};

const getFlavorSortScore = (
  flavorInfo: SelectedFlavorInfo,
  platformAndVariantOptions?: FlavorPlatformAndVariantOption<string>[],
): number => {
  // biome-ignore lint/nursery/noUnnecessaryConditions: legacy code
  if (platformAndVariantOptions) {
    return platformAndVariantOptions.findIndex(
      (option) => option.platform === flavorInfo.platform && option.variant === flavorInfo.variant,
    );
  }
  return flavorInfo.platform === PLATFORMS.DESKTOP ? 0 : 1;
};

const sortSelectedFlavors = ({
  selectedFlavorsInfo,
  campaignConfig,
}: {
  selectedFlavorsInfo: SelectedFlavorInfo[];
  campaignConfig: RegisteredMCCampaignConfig;
}): SelectedFlavorInfo[] => {
  const flavorPlatformAndVariantOptions = campaignConfig.variants?.flavorPlatformAndVariantOptions;

  return selectedFlavorsInfo.toSorted(
    (flavorInfoA, flavorInfoB) =>
      getFlavorSortScore(flavorInfoA, flavorPlatformAndVariantOptions) - getFlavorSortScore(flavorInfoB, flavorPlatformAndVariantOptions),
  );
};

type UseMultiCreativeCampaignContentValue = {
  handleChangeLanguageOption: (value: LanguageOptionValue) => void;
  languageSelectOptions: LanguageSelectOption[];
  error?: string;
  selectedFlavorsInfo: SelectedFlavorInfo[];
  selectedLanguageOptionValue: LanguageOptionValue | undefined;
};

export const useMultiCreativeCampaignContent = (): UseMultiCreativeCampaignContentValue => {
  const history = useHistory();
  const [flavorIndexToErrorCountMap, setFlavorIndexToErrorCountMap] = useState<Record<number, number>>({});
  const flavors = useFlavors();
  const campaignConfig = useCampaignConfig();

  const flavorsInfoExpensive: FlavorInfo[] = useMemo(
    () =>
      flavors.map((flavor, flavorIndex) => {
        const language = flavor.scheduling.language as Language;
        const marketplace = flavor.scheduling.marketplace as Marketplace;
        const platform = flavor.scheduling.platform as PLATFORMS;
        return {
          active: flavor.active,
          contentErrorsCount: flavorIndexToErrorCountMap[flavorIndex] || 0,
          language,
          marketplace,
          optionKey: getLangAndMpKey(marketplace, language),
          originalIndex: flavorIndex,
          platform,
          variant: flavor.variant,
        };
      }),
    [
      flavorIndexToErrorCountMap,
      flavors,
    ],
  );

  const flavorsInfo: FlavorInfo[] = useMemoCompare(flavorsInfoExpensive);

  const languageSelectOptionsMapExpensive = useMemo(() => {
    const newOptionsMap: LanguageSelectOptionsMap = {};
    flavorsInfo.forEach((flavorInfo) => {
      if (!flavorInfo.active) return;
      const existingLanguageOption: LanguageSelectOption | undefined = newOptionsMap[flavorInfo.optionKey];
      if (existingLanguageOption) {
        newOptionsMap[flavorInfo.optionKey] = {
          ...existingLanguageOption,
          errorCount: flavorInfo.contentErrorsCount + existingLanguageOption.errorCount,
        };
        return;
      }
      const marketplace = flavorInfo.marketplace;
      const language = flavorInfo.language as Language;
      const display =
        getLanguageOptionFromLanguageCodeAndMarketPlace(marketplace, language)?.display ||
        `${marketplace} ${LANGUAGE_TO_LANGUAGE_DISPLAY[language]}`;

      newOptionsMap[flavorInfo.optionKey] = {
        errorCount: flavorInfo.contentErrorsCount,
        label: display as DisplayLanguage,
        language,
        marketplace,
        value: flavorInfo.optionKey,
      };
    });
    return newOptionsMap;
  }, [
    flavorsInfo,
  ]);

  const languageSelectOptionsMap = useMemoCompare(languageSelectOptionsMapExpensive);

  const languageSelectOptions = useMemo(
    () => sortBy(Object.values(languageSelectOptionsMap), ({ label }) => label),
    [
      languageSelectOptionsMap,
    ],
  );

  const [selectedLanguageOptionValue, setSelectedLanguageOptionValue] = useState<LanguageOptionValue>(languageSelectOptions?.[0]?.value);

  const selectedLanguageOption = useMemo(
    () => languageSelectOptionsMap[selectedLanguageOptionValue],
    [
      languageSelectOptionsMap,
      selectedLanguageOptionValue,
    ],
  );

  const unsortedSelectedFlavorsInfo: SelectedFlavorInfo[] = useMemo(() => {
    if (!selectedLanguageOption) return [];
    const newSelectedFlavorsInfo: SelectedFlavorInfo[] = flavorsInfo.reduce((acc, flavorInfo) => {
      if (!flavorInfo.active || flavorInfo.optionKey !== selectedLanguageOption.value) {
        return acc;
      }

      // Make a unique key for rendering to make each instance of the content editor unique.
      const variantPiece = flavorInfo.variant ? `-${flavorInfo.variant}` : '';
      const flavorKey = `${flavorInfo.marketplace}-${flavorInfo.language}-${flavorInfo.platform}${variantPiece}-${flavorInfo.originalIndex}`;
      acc.push({
        headerText: getFlavorHeaderText({
          campaignConfig,
          flavorInfo,
          selectedLanguageOption,
        }),
        key: flavorKey,
        language: flavorInfo.language,
        marketplace: flavorInfo.marketplace,
        originalIndex: flavorInfo.originalIndex,
        platform: flavorInfo.platform,
        variant: flavorInfo.variant,
      });

      return acc;
    }, [] as SelectedFlavorInfo[]);
    return newSelectedFlavorsInfo;
  }, [
    campaignConfig,
    flavorsInfo,
    selectedLanguageOption,
  ]);

  const selectedFlavorsInfo = useMemo(
    () =>
      sortSelectedFlavors({
        campaignConfig,
        selectedFlavorsInfo: unsortedSelectedFlavorsInfo,
      }),
    [
      campaignConfig,
      unsortedSelectedFlavorsInfo,
    ],
  );

  const languagesErrorMessage = useMemo(() => {
    const errorCount = languageSelectOptions.reduce((acc, languageOption) => {
      if (languageOption.errorCount > 0) return acc + 1;
      return acc;
    }, 0);
    if (!errorCount) return;
    return `${pluralize('language', errorCount, true)} with errors`;
  }, [
    languageSelectOptions,
  ]);

  const { error: campaignError } = useTPCampaignContext();

  const error = languagesErrorMessage ?? campaignError?.message.toString();

  const handleChangeLanguageOption = useCallback((value: LanguageOptionValue) => {
    setSelectedLanguageOptionValue(value);
  }, []);

  const validationOutput = useValidateAllFlavorContent({
    cacheBuster: `${selectedLanguageOptionValue}`,
  });

  useEffect(() => {
    setFlavorIndexToErrorCountMap(validationOutput);
  }, [
    validationOutput,
  ]);

  useEffect(() => {
    const { hash, pathname } = history.location;
    if (hash.includes('#content-editor-')) {
      const languageOption = hash.split('#content-editor-')[1] as LanguageOptionValue;
      handleChangeLanguageOption(languageOption);
      history.replace(pathname);
    }
  }, [
    handleChangeLanguageOption,
    history,
    history.location,
  ]);

  useEffect(() => {
    if (selectedFlavorsInfo.length < 1 && languageSelectOptions.length > 0) {
      setSelectedLanguageOptionValue(languageSelectOptions[0].value);
    }
  }, [
    languageSelectOptions,
    selectedFlavorsInfo.length,
  ]);

  return useMemo(
    () => ({
      error,
      handleChangeLanguageOption,
      languageSelectOptions,
      selectedFlavorsInfo,
      selectedLanguageOptionValue,
    }),
    [
      handleChangeLanguageOption,
      languageSelectOptions,
      error,
      selectedLanguageOptionValue,
      selectedFlavorsInfo,
    ],
  );
};
